/**
 * ContactFormDialog
 * =====================================================================
 * Consolidates the formerly-duplicated "Add New Contact" and "Edit
 * Contact" dialogs that lived inline in `App.js` (lines ~54505 and
 * ~54657 — ~360 lines of near-identical JSX).
 *
 * Parent still owns `formData` + `onChange` so we don't have to touch
 * the Dashboard's contact-form state machine.  The only behavior gate
 * is the address block, which existed only on Edit before — kept
 * edit-only here to preserve verbatim behavior.
 *
 * Feb 2026 refactor.  Zero behavior change vs the pre-extraction
 * dialogs.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { DebouncedInput } from '../ui/debounced-textarea';

const RELATIONSHIPS = [
  ['parent', 'Parent'],
  ['guardian', 'Guardian'],
  ['spouse', 'Spouse'],
  ['sibling', 'Sibling'],
  ['child', 'Child'],
  ['friend', 'Friend'],
  ['caregiver', 'Caregiver'],
  ['pcp', 'PCP (Primary Care)'],
  ['other_provider', 'Other Provider'],
  ['other', 'Other'],
];

export const ContactFormDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  onCancel,
  editing = false,
}) => {
  const idPrefix = editing ? 'edit-contact' : 'contact';
  const patch = (k, v) => setFormData({ ...formData, [k]: v });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`${idPrefix}-name`}>Name</Label>
              <DebouncedInput
                id={`${idPrefix}-name`}
                type="text"
                value={formData.name}
                onChange={(e) => patch('name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}-relationship`}>Relationship</Label>
              <Select
                value={formData.relationship}
                onValueChange={(v) => patch('relationship', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`${idPrefix}-title-agency`}>Title / Agency</Label>
              <DebouncedInput
                id={`${idPrefix}-title-agency`}
                type="text"
                value={formData.title_agency || ''}
                onChange={(e) => patch('title_agency', e.target.value)}
                placeholder="e.g. MD — UMass Family Medicine"
                data-testid={`${idPrefix}-title-agency-input`}
              />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}-phone`}>Phone</Label>
              <DebouncedInput
                id={`${idPrefix}-phone`}
                type="tel"
                value={formData.phone}
                onChange={(e) => patch('phone', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}-email`}>Email</Label>
              <DebouncedInput
                id={`${idPrefix}-email`}
                type="text"
                value={formData.email}
                onChange={(e) => patch('email', e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}-fax`}>Fax</Label>
              <DebouncedInput
                id={`${idPrefix}-fax`}
                type="tel"
                value={formData.fax || ''}
                onChange={(e) => patch('fax', e.target.value)}
                placeholder="(555) 123-4567"
                data-testid={`${idPrefix}-fax-input`}
              />
            </div>
          </div>

          {/* Address block — Jun 2026: on BOTH Add and Edit; street full
              width, then City/State/Zip on one row. */}
          <div className="border-t pt-4">
              <div className="flex items-center space-x-2 mb-4">
                <input
                  id={`${idPrefix}-same-household`}
                  type="checkbox"
                  checked={formData.lives_in_same_household}
                  onChange={(e) =>
                    patch('lives_in_same_household', e.target.checked)
                  }
                />
                <Label htmlFor={`${idPrefix}-same-household`}>
                  Lives in the same household as client
                </Label>
              </div>
              {!formData.lives_in_same_household && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="md:col-span-3">
                    <Label htmlFor={`${idPrefix}-address`}>Contact Address</Label>
                    <DebouncedInput
                      id={`${idPrefix}-address`}
                      type="text"
                      value={formData.contact_address}
                      onChange={(e) => patch('contact_address', e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${idPrefix}-city`}>City</Label>
                    <DebouncedInput
                      id={`${idPrefix}-city`}
                      type="text"
                      value={formData.contact_city}
                      onChange={(e) => patch('contact_city', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${idPrefix}-state`}>State</Label>
                    <DebouncedInput
                      id={`${idPrefix}-state`}
                      type="text"
                      value={formData.contact_state}
                      onChange={(e) => patch('contact_state', e.target.value)}
                      maxLength="2"
                      placeholder="MA"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${idPrefix}-zipcode`}>Zip Code</Label>
                    <DebouncedInput
                      id={`${idPrefix}-zipcode`}
                      type="text"
                      value={formData.contact_zipcode}
                      onChange={(e) => patch('contact_zipcode', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

          {/* Checkbox cluster — identical in both dialogs. */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                id={`${idPrefix}-can-receive-info`}
                type="checkbox"
                checked={formData.can_receive_info}
                onChange={(e) => patch('can_receive_info', e.target.checked)}
              />
              <Label htmlFor={`${idPrefix}-can-receive-info`}>
                Can receive information
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id={`${idPrefix}-release-on-file`}
                type="checkbox"
                checked={formData.release_on_file}
                onChange={(e) => patch('release_on_file', e.target.checked)}
              />
              <Label htmlFor={`${idPrefix}-release-on-file`}>Release on file</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id={`${idPrefix}-is-minor-or-legal-guardian`}
                type="checkbox"
                checked={formData.is_minor_or_legal_guardian}
                onChange={(e) =>
                  patch('is_minor_or_legal_guardian', e.target.checked)
                }
              />
              <Label htmlFor={`${idPrefix}-is-minor-or-legal-guardian`}>
                Client is a minor/has legal guardian (release not necessary)
              </Label>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
              {editing ? 'Update Contact' : 'Add Contact'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
